// A BYO-producer that mirrors a site's blobs into an external RASL bucket (szzt-spec.md
// §7.3, §13.1) — the CDN half of items 6/7. It maps each content block to the RASL key convention
// `.well-known/rasl/<cid>` and, on a fully-successful push, advertises the bucket's public host as a
// bare-host `hints` entry so any gateway can race it (`gateway/src/mirror.ts` raslWellKnownUrl).
//
// This module is deliberately cloud-agnostic: WHERE the bytes go and HOW the request is authenticated
// (S3/R2 SigV4, a presigned URL, a Worker upload endpoint + token) live entirely in the injected
// `BucketPut`. A RASL bucket is CID-exact by construction — the object is keyed by the content CID and
// the gateway re-hashes every byte on read — so unlike IPFS there is no codec/chunking trap here.
import type { MirrorBlock, MirrorProducer } from "./types.ts";

/**
 * Stores `bytes` at object key `key` in an external bucket, resolving on success and REJECTING on any
 * failure (a rejection aborts the whole publish's push → all-or-nothing → no hint written). The
 * credential/signing model is the deployment's choice; this seam only sees the key, bytes, and type.
 * `key` has no leading slash (e.g. `.well-known/rasl/bafy…`); the impl joins it to its own endpoint.
 */
export type BucketPut = (key: string, bytes: Uint8Array, contentType: string) => Promise<void>;

/** RASL object-key prefix (§13.1). The served URL is `https://<host>/<RASL_KEY_PREFIX><cid>`; this
 *  MUST stay in lockstep with the gateway's `raslWellKnownUrl` serve route. No leading slash — object
 *  stores key without one. */
export const RASL_KEY_PREFIX = ".well-known/rasl/";

/** Object key a block is stored under in a RASL bucket. */
export function raslKey(cid: string): string {
  return `${RASL_KEY_PREFIX}${cid}`;
}

/** Content-type to store a block under: the resource's own MIME, or a codec default for the manifest. */
function blockContentType(block: MirrorBlock): string {
  if (block.contentType !== undefined) return block.contentType;
  return block.codec === "dag-cbor" ? "application/vnd.ipld.dag-cbor" : "application/octet-stream";
}

/**
 * Build a {@link MirrorProducer} that mirrors blocks into a RASL bucket reachable at `host` (a bare
 * host like `cdn.example.com`, served over https). Every block — resource blobs AND the manifest — is
 * PUT at `.well-known/rasl/<cid>` via `put`; on full success `hints()` returns `[host]` so the site
 * record advertises the mirror. A `put` rejection propagates (publish()'s push is all-or-nothing).
 */
export function raslBucketProducer(opts: { host: string; put: BucketPut }): MirrorProducer {
  const { host, put } = opts;
  return {
    async pushBlock(block: MirrorBlock): Promise<void> {
      await put(raslKey(block.cid), block.bytes, blockContentType(block));
    },
    hints(): string[] {
      return [host];
    },
  };
}
